import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { SEO } from '@/components/common/SEO';
import { Hero } from '@/components/common/Hero';
import { ServiceIconBox } from '@/components/common/ServiceIcon';
import { PageLoader } from '@/components/ui/Spinner';
import { useService, useServices } from '@/hooks/useServices';
import { useTranslation } from '@/features/i18n';
import { cn } from '@/lib/utils';

export function ServiceDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: service, isLoading, error } = useService(slug || '');
  const { data: allServices } = useServices();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const { t } = useTranslation();

  if (isLoading) {
    return <PageLoader />;
  }

  if (error || !service) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#edf1f5]">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-black mb-4">
            {t.services.notFound.title}
          </h1>
          <p className="text-[#666] mb-6">
            {t.services.notFound.description}
          </p>
          <Link to="/services" className="ninito-btn ninito-btn-primary">
            {t.services.viewAll}
          </Link>
        </div>
      </div>
    );
  }

  const otherServices = allServices?.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <>
      <SEO
        title={service.seo_title || service.title}
        description={service.seo_description || service.short_description || ''}
        keywords={service.seo_keywords || ''}
        ogImage={service.og_image_url || service.hero_image_url || ''}
      />

      <Hero
        title={service.title}
        subtitle={t.services.service}
        description={service.short_description || ''}
        size="md"
        variant="light"
      >
        <Link to="/contact" className="ninito-btn ninito-btn-primary ninito-btn-lg group">
          {service.cta_text || t.common.getStarted}
          <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </Hero>

      {/* Main content - ninito style */}
      <section className="ninito-section bg-[#edf1f5]">
        <div className="ninito-container">
          <div className="grid lg:grid-cols-3 gap-10 lg:gap-16">
            {/* Main column */}
            <div className="lg:col-span-2 space-y-12">
              {service.long_description && (
                <div
                  className="prose prose-lg max-w-none [&_h2]:text-black [&_h2]:font-semibold [&_h2]:tracking-tight [&_h3]:text-black [&_p]:text-[#666] [&_li]:text-[#666] [&_a]:text-[#0078ff] [&_strong]:text-black"
                  dangerouslySetInnerHTML={{ __html: service.long_description }}
                />
              )}

              {/* Features - ninito card style */}
              {service.features_json && service.features_json.length > 0 && (
                <div>
                  <h2 className="text-2xl font-semibold text-black tracking-tight mb-6">
                    {t.services.featuresTitle}
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-5">
                    {service.features_json.map((feature, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        viewport={{ once: true }}
                      >
                        <div className="ninito-card h-full">
                          <div className="flex items-start">
                            <div className="w-8 h-8 rounded-full bg-[#0078ff]/10 flex items-center justify-center flex-shrink-0 mr-4">
                              <Check className="w-4 h-4 text-[#0078ff]" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-black">
                                {feature.title}
                              </h3>
                              <p className="text-sm text-[#666] mt-1 leading-relaxed">
                                {feature.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Benefits - ninito style */}
              {service.benefits_json && service.benefits_json.length > 0 && (
                <div>
                  <h2 className="text-2xl font-semibold text-black tracking-tight mb-6">
                    {t.services.benefits}
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-6">
                    {service.benefits_json.map((benefit, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        viewport={{ once: true }}
                        className="flex items-start"
                      >
                        <div className="w-10 h-10 rounded-2xl bg-[#0078ff]/10 flex items-center justify-center mr-4 flex-shrink-0">
                          <span className="text-[#0078ff] font-bold text-sm">
                            {String(index + 1).padStart(2, '0')}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-black">
                            {benefit.title}
                          </h3>
                          <p className="text-sm text-[#666] mt-1 leading-relaxed">
                            {benefit.description}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* FAQ - ninito accordion style */}
              {service.faq_json && service.faq_json.length > 0 && (
                <div>
                  <h2 className="text-2xl font-semibold text-black tracking-tight mb-6">
                    {t.services.faq}
                  </h2>
                  <div className="space-y-4">
                    {service.faq_json.map((faq, index) => (
                      <div key={index} className="ninito-card p-0 overflow-hidden">
                        <button
                          onClick={() => setOpenFaq(openFaq === index ? null : index)}
                          className="w-full flex items-center justify-between p-5 text-left hover:bg-black/[0.02] transition-colors"
                        >
                          <span className="font-medium text-black pr-4">
                            {faq.question}
                          </span>
                          <ChevronDown
                            className={cn(
                              'w-5 h-5 text-[#999] transition-transform flex-shrink-0',
                              openFaq === index && 'rotate-180'
                            )}
                          />
                        </button>
                        {openFaq === index && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="px-5 pb-5"
                          >
                            <p className="text-[#666] leading-relaxed">
                              {faq.answer}
                            </p>
                          </motion.div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-32 space-y-6">
                {/* CTA Card - ninito style */}
                <div className="ninito-card bg-[#0078ff]/5 border border-[#0078ff]/10">
                  <h3 className="text-lg font-semibold text-black mb-2">
                    {t.services.cta.interested}
                  </h3>
                  <p className="text-[#666] text-sm mb-5 leading-relaxed">
                    {t.services.cta.interestedDesc}
                  </p>
                  <Link
                    to="/contact"
                    className="ninito-btn ninito-btn-primary w-full justify-center"
                  >
                    {t.services.cta.button}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* Other services - ninito style */}
                {otherServices && otherServices.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-black mb-4">
                      {t.services.otherServices}
                    </h3>
                    <div className="space-y-3">
                      {otherServices.map((s) => (
                        <Link
                          key={s.id}
                          to={`/services/${s.slug}`}
                          className="flex items-center p-4 rounded-2xl bg-white hover:shadow-md transition-all group"
                        >
                          <ServiceIconBox icon={s.icon} size="sm" />
                          <span className="ml-3 font-medium text-black group-hover:text-[#0078ff] transition-colors">
                            {s.title}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - ninito dark style */}
      <section className="ninito-cta">
        <div className="relative ninito-container">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-[32px] md:text-[40px] lg:text-[48px] font-semibold text-white tracking-[-0.02em]"
          >
            {t.services.cta.ready}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-5 text-lg md:text-xl text-[#999] max-w-[600px] mx-auto"
          >
            {t.services.cta.readyDesc} {service.title.toLowerCase()} {t.services.cta.readyDescSuffix}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-10"
          >
            <Link to="/contact" className="ninito-btn ninito-btn-white ninito-btn-lg group">
              {service.cta_text || t.common.getStarted}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
